/**
 * Trainer items applied to an owned mon — pure, deterministic, no rendering/IO.
 *
 * Two item families for the MVP:
 *  - Rare Candy: instantly levels a mon up by one, reusing the exact same level-up
 *    application as battle XP (so stat recompute + newly-learnable moves match a
 *    real in-battle level-up). No-op at LEVEL_MAX.
 *  - Vitamins (HP Up / Protein / Iron / Calcium / Zinc / Carbos): each adds +10 EV
 *    to one stat, respecting the standard 252-per-stat and 510-total caps.
 *
 * These mutate the {@link PokemonInstance} in place (level/exp/evs/currentHp + the
 * cached maxHp), the same convention `makeLevelUpEvents` uses. They never touch IO.
 */
import type { PokemonInstance, SpeciesData, StatKey, EVs } from '@/shared';
import { LEVEL_MAX, STAT_KEYS, EV_MAX_PER_STAT, EV_MAX_TOTAL } from '@/shared';
import { computeStats, getNature } from './stats.js';
import { expForLevel, makeLevelUpEvents } from './leveling.js';

/** Result of feeding a Rare Candy. */
export interface RareCandyResult {
  /** True if the mon actually gained a level (false only at LEVEL_MAX). */
  readonly leveled: boolean;
  /** The mon's level after the candy (unchanged at LEVEL_MAX). */
  readonly newLevel: number;
  /** Moves newly learnable at the new level — the renderer offers accept/replace. */
  readonly learnableMoves: readonly string[];
}

/**
 * Feed a Rare Candy: if below LEVEL_MAX, level the mon up by exactly one.
 *
 * Sets `exp` to the threshold of `level + 1` and reuses {@link makeLevelUpEvents}
 * (the same path battle XP uses) so the stat recompute, cached `maxHp`, and the
 * `currentHp` bump (only the HP gained — a damaged mon is not fully healed) all
 * match a real in-battle level-up. No-op (leveled:false) at LEVEL_MAX.
 */
export function applyRareCandy(instance: PokemonInstance, species: SpeciesData): RareCandyResult {
  if (instance.level >= LEVEL_MAX) {
    return { leveled: false, newLevel: instance.level, learnableMoves: [] };
  }

  const nature = getNature(instance.natureId);
  const targetExp = expForLevel(species.growthRate, instance.level + 1);
  const events = makeLevelUpEvents(instance, targetExp, species, nature);
  const learnableMoves = events.length > 0 ? events[0].newMoves : [];

  return { leveled: true, newLevel: instance.level, learnableMoves };
}

/** Result of using a vitamin. */
export interface VitaminResult {
  /** EVs actually added (0–10) after clamping to the per-stat and total caps. */
  readonly added: number;
  /** True if a cap (252 single / 510 total) prevented the full +10. */
  readonly capped: boolean;
}

/** How much a single vitamin adds before clamping. */
const VITAMIN_EV_STEP = 10;

/**
 * Use a vitamin: add +10 EV to `stat`, clamped so neither the 252-per-stat cap nor
 * the 510-total cap is exceeded. If the HP EV changed, recompute + cache `maxHp` and
 * raise `currentHp` by the gain (only the gained HP — never a full heal).
 *
 * `species` is needed only to recompute HP when the HP EV changes (the HP stat depends
 * on base HP); other stats are not cached on the instance so they need no recompute here.
 * Returns how many EVs were actually added and whether a cap clamped the +10.
 */
export function applyVitamin(
  instance: PokemonInstance,
  stat: StatKey,
  species?: SpeciesData,
): VitaminResult {
  const current = instance.evs[stat];
  const total = (STAT_KEYS as readonly StatKey[]).reduce((sum, k) => sum + instance.evs[k], 0);

  const perStatRoom = EV_MAX_PER_STAT - current;
  const totalRoom = EV_MAX_TOTAL - total;
  const added = Math.max(0, Math.min(VITAMIN_EV_STEP, perStatRoom, totalRoom));
  const capped = added < VITAMIN_EV_STEP;

  if (added === 0) {
    return { added: 0, capped };
  }

  const nextEvs: EVs = { ...instance.evs, [stat]: current + added };

  if (stat === 'hp' && species) {
    const nature = getNature(instance.natureId);
    const beforeHp = computeStats(
      species.baseStats,
      instance.ivs,
      instance.evs,
      instance.level,
      nature,
    ).hp;
    instance.evs = nextEvs;
    const afterHp = computeStats(
      species.baseStats,
      instance.ivs,
      instance.evs,
      instance.level,
      nature,
    ).hp;
    instance.maxHp = afterHp;
    instance.currentHp += afterHp - beforeHp;
  } else {
    instance.evs = nextEvs;
  }

  return { added, capped };
}

/** Maps a vitamin item to the EV stat it trains, with a display label. */
export interface Vitamin {
  readonly itemId: string;
  readonly stat: StatKey;
  readonly label: string;
}

/** The six standard vitamins, in canonical stat order (HP → Speed). */
export const VITAMINS: readonly Vitamin[] = [
  { itemId: 'hp-up', stat: 'hp', label: 'HP Up' },
  { itemId: 'protein', stat: 'atk', label: 'Protein' },
  { itemId: 'iron', stat: 'def', label: 'Iron' },
  { itemId: 'calcium', stat: 'spatk', label: 'Calcium' },
  { itemId: 'zinc', stat: 'spdef', label: 'Zinc' },
  { itemId: 'carbos', stat: 'spe', label: 'Carbos' },
];

/**
 * The evolution-stone itemIds the dataset's item-evolutions use (trigger:'item'),
 * in display order. These are the stones the Training Grounds bench offers in
 * unlimited supply; each is resolved against a species' evolutions via
 * core/evolution `checkEvolution(mon, species, 'item', { itemId })`. Kept here next
 * to VITAMINS so all bench-item ids have one home; the dataset is the source of truth
 * for which actually evolve a given species. (e.g. 'water-stone' → eevee→vaporeon,
 * 'thunder-stone' → pikachu→raichu.)
 */
export const EVOLUTION_STONES: readonly string[] = [
  'fire-stone',
  'water-stone',
  'thunder-stone',
  'leaf-stone',
  'moon-stone',
  'sun-stone',
  'ice-stone',
  'shiny-stone',
  'dusk-stone',
  'dawn-stone',
  'black-augurite',
  'galarica-cuff',
  'galarica-wreath',
  'peat-block',
];
